/**
 * Single source of truth for API base URLs.
 *
 * Resolution order per service:
 *   1. `window.<service>Api` runtime global (allows operators / index.html
 *      snippets / runtime overrides to win without a rebuild).
 *   2. `import.meta.env.VITE_<SERVICE>_API_BASE_URL` build-time variable
 *      sourced from `.env*` (Vite inlines this at build).
 *   3. Built-in fallback (the production host), trimmed of trailing `/`.
 *
 * `getAppApiBase()` extends this chain: when neither the runtime global
 * nor the env var is set, it falls back to the LYT Accounting base so the
 * App API surface stays reachable on dev (no `localhost:5173` 404s).
 *
 * No other module should import `import.meta.env.VITE_*` for base URLs
 * directly. Add new services here so a URL change is a one-line edit.
 */

const PROD_FALLBACK_LYT = 'http://localhost:8282/lyt-accounting'
// const PROD_FALLBACK_LYT = 'https://lytningtomcat.chrilantech.com/lyt-accounting'
const PROD_FALLBACK_SUBSCRIPTION = 'https://overflow.abc.co.zm/SubscriptionManager/api'

export function getLytAccountingApiBase(): string {
  const raw =
    (typeof window !== 'undefined' && window.lytAccountingApi) ||
    (import.meta.env.VITE_LYT_ACCOUNTING_API_BASE_URL as string | undefined) ||
    PROD_FALLBACK_LYT
  return raw.replace(/\/+$/, '')
}

export function getSubscriptionManagerApiBase(): string {
  const raw =
    (typeof window !== 'undefined' && window.subscriptionManagerApi) ||
    (import.meta.env.VITE_SUBSCRIPTION_MANAGER_API_BASE_URL as string | undefined) ||
    PROD_FALLBACK_SUBSCRIPTION
  return raw.replace(/\/+$/, '')
}

export function getAppApiBase(): string {
  // App API has no production host of its own — fall back to the LYT
  // Accounting base so dev hits the local backend at localhost:8282 by
  // default rather than producing a same-origin 404 on the Vite port.
  const lyt = getLytAccountingApiBase()
  const raw =
    (typeof window !== 'undefined' && window.appApi) ||
    (import.meta.env.VITE_APP_API_BASE_URL as string | undefined) ||
    lyt
  return raw.replace(/\/+$/, '')
}